import { HpRequestOptions } from '@avantia/client-and-server-utilities';
import { HpError } from '@avantia/logging-and-errors';
import { logError } from '../clientLogging';
import { getConfig } from '../config/configuration';
import { fetchResolver } from '../engineClient/actions/fetchResolver';
import { getAnswersFromAnswerMap } from '../engineClient/tools/clientAndServerUtilities';
import { HpCreateActionPromisesProps, HpDependentActionFunction } from '../engineClient/types';
import { HpClaimsAction } from '../types/actionTypes';
import { questionIds } from '../types/questionConstants';

export interface HpBankDetailsValidationResponse {
  result: string;
}

export interface HpBankDetailsValidationFailedPayload {
  errorMessage: string;
  invalidQuestionId: string;
}

// eslint-disable-next-line @typescript-eslint/no-empty-interface
export interface HpBankDetailsValidationPassedPayload {
  // Empty.
}

const { websiteEndpoint } = getConfig();

type BankDetailsValidationFailedErrorCode =
  | 'INVALID - Sortcode'
  | 'INVALID - Account'
  | 'INVALID - Modulus Check Failed';

const errorMessages: Record<BankDetailsValidationFailedErrorCode, string> = {
  ['INVALID - Sortcode']: 'Please check your Sort Code',
  ['INVALID - Account']: 'Please check your Account Number',
  ['INVALID - Modulus Check Failed']: 'Please check your Account Number and Sort Code'
};

export function validateBankDetailsAction(
  properties: HpCreateActionPromisesProps
): HpDependentActionFunction<HpBankDetailsValidationPassedPayload | HpBankDetailsValidationFailedPayload> | undefined {
  const { questionSet } = properties;
  const { answers } = questionSet;
  const { productIdDefault } = getConfig();

  const { sortCode, accountNumber } = getAnswersFromAnswerMap({
    answers,
    keys: ['sortCode', 'accountNumber'],
    options: { returnAnyAnswerType: true },
    productId: productIdDefault
  });

  if (!sortCode || !accountNumber) {
    return;
  }

  const options: HpRequestOptions = {
    url: `${websiteEndpoint}/api/validate-bank-details?sortCode=${sortCode}&accountNumber=${accountNumber}`,
    method: 'POST',
    description: 'Dependent action validate-bank-details',
    timeout: 5000
  };

  const validateBankDetailsAction: HpDependentActionFunction<
    HpBankDetailsValidationPassedPayload | HpBankDetailsValidationFailedPayload
  > = (dispatch) => {
    return new Promise<boolean>((resolve, reject) => {
      fetchResolver<unknown, HpBankDetailsValidationResponse>({
        options,
        loadingPageType: 'overlay',
        dispatch,
        successAction: (resp) => {
          const { result } = resp;
          if (result.startsWith('VALID')) {
            const action: HpClaimsAction<HpBankDetailsValidationPassedPayload> = {
              type: 'BANK_DETAILS_VALIDATION_PASSED',
              payload: { modalQuestionId: properties.question.questionId }
            };
            dispatch(action as any);
            return resolve(true);
          } else {
            const errorMessage = errorMessages[result] || result;

            if (errorMessage) {
              let invalidQuestionId: string | null = null;
              if (errorMessage.toLowerCase().includes('account number')) {
                invalidQuestionId = questionIds.accountNumber;
              } else {
                invalidQuestionId = questionIds.sortCode;
              }

              const action: HpClaimsAction<HpBankDetailsValidationFailedPayload> = {
                type: 'BANK_DETAILS_VALIDATION_FAILED',
                payload: { errorMessage, invalidQuestionId }
              };
              dispatch(action as any);
              return resolve(false);
            }
          }
        },
        failedAction: (reason) => {
          const message = `failed to validate bank details: ${new HpError(reason).message}`;
          logError(message);
        }
      })
        .then(() => {
          return resolve(true);
        })
        .catch(reject);
    });
  };

  return validateBankDetailsAction;
}
